import { useReCaptcha } from './GoogleReCaptcha'

/**
 * Hook for validating reCAPTCHA before form submission
 * Returns a validation function that throws errors on failure
 *
 * @example
 * const { validateRecaptcha } = useReCaptchaValidation()
 *
 * try {
 *   const token = await validateRecaptcha()
 *   // Proceed with form submission
 * } catch (error) {
 *   // Handle validation error
 * }
 */
export const useReCaptchaValidation = () => {
    const { execute, isLoaded } = useReCaptcha()

    /**
     * Validates reCAPTCHA and returns the token
     * @param action - The reCAPTCHA action (default: 'submit')
     * @returns Promise that resolves with the reCAPTCHA token
     * @throws Error if validation fails
     */
    const validateRecaptcha = async (
        action: string = 'submit'
    ): Promise<string> => {
        if (!isLoaded) {
            throw new Error(
                'Security verification is loading. Please try again.'
            )
        }

        const token = await execute(action)

        if (!token) {
            throw new Error('Security verification failed. Please try again.')
        }

        return token
    }

    return { validateRecaptcha, isLoaded }
}
